import { FC, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { CircularProgress } from '@mui/material';
import { BookingUnitType, IHotelValidationResult } from '@services';
import { T_KEYS } from '../../constants';

// External link icon
const ExternalLinkIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 8.66667V12.6667C12 13.0203 11.8595 13.3594 11.6095 13.6095C11.3594 13.8595 11.0203 14 10.6667 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V5.33333C2 4.97971 2.14048 4.64057 2.39052 4.39052C2.64057 4.14048 2.97971 4 3.33333 4H7.33333" stroke="#0052CC" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M10 2H14V6" stroke="#0052CC" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M6.66669 9.33333L14 2" stroke="#0052CC" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// Plus icon for add button
const PlusIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M10 4.16667V15.8333" stroke="#5B2FF9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M4.16669 10H15.8334" stroke="#5B2FF9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// Trash icon for delete button
const TrashIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2.5 5H4.16667H17.5" stroke="#999999" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M6.66669 5V3.33333C6.66669 2.89131 6.84228 2.46738 7.15484 2.15482C7.4674 1.84226 7.89133 1.66667 8.33335 1.66667H11.6667C12.1087 1.66667 12.5326 1.84226 12.8452 2.15482C13.1578 2.46738 13.3334 2.89131 13.3334 3.33333V5M15.8334 5V16.6667C15.8334 17.1087 15.6578 17.5326 15.3452 17.8452C15.0326 18.1577 14.6087 18.3333 14.1667 18.3333H5.83335C5.39133 18.3333 4.9674 18.1577 4.65484 17.8452C4.34228 17.5326 4.16669 17.1087 4.16669 16.6667V5H15.8334Z" stroke="#999999" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// Error icon
const ErrorIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8 14C11.3137 14 14 11.3137 14 8C14 4.68629 11.3137 2 8 2C4.68629 2 2 4.68629 2 8C2 11.3137 4.68629 14 8 14Z" stroke="#D32F2F" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M8 5.33333V8" stroke="#D32F2F" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M8 10.6667H8.00667" stroke="#D32F2F" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// Verified checkmark icon
const VerifiedIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M13.3334 4L6.00002 11.3333L2.66669 8" stroke="#4CAF50" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// Verification status for each field
type VerificationStatus = 'idle' | 'verifying' | 'verified' | 'error';

// Property ID field state
interface PropertyIdField {
  id: string;
  value: string;
  error?: string;
  verificationStatus: VerificationStatus;
  lastVerifiedValue?: string;
  channelId?: string; // Store the channel ID from successful verification
}

interface BookingPropertyIdStepProps {
  unitType: BookingUnitType;
  onSubmit: (propertyIds: string[]) => void;
  onSubmitSingle?: (propertyId: string) => void;
  onVerifySingle?: (propertyId: string) => Promise<{ isValid: boolean; error?: string; channelId?: string }>;
  onAutoImport?: (hotelIds: string[]) => void; // For 'single' unitType - import all verified hotel IDs
  isLoading: boolean;
  error?: string;
  validationResults?: IHotelValidationResult[];
  hasValidationErrors?: boolean;
}

const BookingPropertyIdStep: FC<BookingPropertyIdStepProps> = ({
  unitType,
  onSubmit,
  onSubmitSingle,
  onVerifySingle,
  onAutoImport,
  isLoading,
  error,
  validationResults,
  hasValidationErrors,
}) => {
  const { t } = useTranslation();
  // For 'single' unit type - multiple property IDs
  const [propertyIds, setPropertyIds] = useState<PropertyIdField[]>([
    { id: '1', value: '', error: undefined, verificationStatus: 'idle', lastVerifiedValue: undefined }
  ]);

  // For 'multi' unit type - single property ID (backward compatibility)
  const [singlePropertyId, setSinglePropertyId] = useState('');

  // Check if a single property ID is valid
  const isValidPropertyId = (value: string) => value.length >= 5 && /^\d+$/.test(value);

  // Check if all property IDs are valid for multi-ID mode
  const hasValidPropertyIds = propertyIds.some(field => isValidPropertyId(field.value));
  
  // Check if the single property ID is valid
  const isSinglePropertyIdValid = isValidPropertyId(singlePropertyId);

  // Update validation errors from API response
  const getFieldError = useCallback((hotelId: string): string | undefined => {
    if (!validationResults) return undefined;
    const result = validationResults.find(r => r.hotelId === hotelId);
    if (result && !result.isValid) {
      return result.errorMessageAr || result.errorMessageEn || t(T_KEYS.INVALID_ID_ERROR);
    }
    return undefined;
  }, [validationResults]);

  const handlePropertyIdChange = useCallback((id: string, value: string) => {
    // Only allow digits
    const cleanValue = value.replace(/\D/g, '');
    setPropertyIds(prev => prev.map(field => {
      if (field.id !== id) return field;
      // Reset verification status if value changed from last verified value
      const needsReverification = field.lastVerifiedValue !== cleanValue;
      return { 
        ...field, 
        value: cleanValue, 
        error: undefined,
        verificationStatus: needsReverification ? 'idle' : field.verificationStatus,
      };
    }));
  }, []);

  const handleSinglePropertyIdChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    setSinglePropertyId(value);
  }, []);

  const handleAddPropertyId = useCallback(() => {
    const newId = String(Date.now());
    setPropertyIds(prev => [...prev, { id: newId, value: '', error: undefined, verificationStatus: 'idle', lastVerifiedValue: undefined }]);
  }, []);

  const handleRemovePropertyId = useCallback((id: string) => {
    setPropertyIds(prev => {
      if (prev.length <= 1) return prev;
      return prev.filter(field => field.id !== id);
    });
  }, []);

  // Handle blur verification for a single property ID field
  const handlePropertyIdBlur = useCallback(async (id: string) => {
    if (!onVerifySingle) return;

    const field = propertyIds.find(f => f.id === id);
    if (!field) return;

    // Skip if empty or invalid format
    if (!isValidPropertyId(field.value)) return;

    // Skip if already verified with the same value
    if (field.verificationStatus === 'verified' && field.lastVerifiedValue === field.value) return;

    // Skip if currently verifying
    if (field.verificationStatus === 'verifying') return;

    // Check for duplicate IDs (same value in another field)
    const isDuplicate = propertyIds.some(f => f.id !== id && f.value === field.value && f.value !== '');
    if (isDuplicate) {
      setPropertyIds(prev => prev.map(f => 
        f.id === id ? { ...f, error: t(T_KEYS.DUPLICATE_PROPERTY_ID) || 'رقم العقار مكرر', verificationStatus: 'error' } : f
      ));
      return;
    }

    // Set verifying status
    setPropertyIds(prev => prev.map(f => 
      f.id === id ? { ...f, verificationStatus: 'verifying', error: undefined } : f
    ));

    try {
      const result = await onVerifySingle(field.value);
      
      setPropertyIds(prev => prev.map(f => {
        if (f.id !== id) return f;
        // Only update if value hasn't changed during verification
        if (f.value !== field.value) return f;
        
        return {
          ...f,
          verificationStatus: result.isValid ? 'verified' : 'error',
          lastVerifiedValue: result.isValid ? field.value : f.lastVerifiedValue,
          channelId: result.isValid ? result.channelId : f.channelId,
          error: result.isValid ? undefined : result.error,
        };
      }));
    } catch (err) {
      setPropertyIds(prev => prev.map(f => 
        f.id === id ? { 
          ...f, 
          verificationStatus: 'error', 
          error: t(T_KEYS.VERIFICATION_FAILED) || 'فشل التحقق من رقم العقار'
        } : f
      ));
    }
  }, [propertyIds, onVerifySingle, t]);

  const handleSubmit = useCallback(async () => {
    if (isLoading) return;

    if (unitType === 'single') {
      // Multiple property IDs flow - auto-import after verification
      const validFields = propertyIds.filter(field => isValidPropertyId(field.value));
      
      if (validFields.length === 0) return;

      // Check if all valid fields are already verified
      const allVerified = validFields.every(field => 
        field.verificationStatus === 'verified' && field.lastVerifiedValue === field.value
      );

      if (allVerified && onAutoImport) {
        // All properties are verified - import all hotel IDs
        const hotelIds = validFields.map(field => field.value);
        onAutoImport(hotelIds);
        return;
      }

      // Find unverified fields that need verification
      const unverifiedFields = validFields.filter(field => 
        field.verificationStatus !== 'verified' || field.lastVerifiedValue !== field.value
      );

      if (unverifiedFields.length > 0 && onVerifySingle) {
        // Verify only unverified fields
        for (const field of unverifiedFields) {
          await handlePropertyIdBlur(field.id);
        }
      } else {
        // Fallback to original submit behavior
        onSubmit(validFields.map(field => field.value));
      }
    } else {
      // Multi-unit hotel flow - navigate to import page after verification
      if (isSinglePropertyIdValid && onSubmitSingle) {
        onSubmitSingle(singlePropertyId);
      } else if (isSinglePropertyIdValid) {
        onSubmit([singlePropertyId]);
      }
    }
  }, [isLoading, unitType, propertyIds, singlePropertyId, isSinglePropertyIdValid, onSubmit, onSubmitSingle, onVerifySingle, onAutoImport, handlePropertyIdBlur]);

  const handleKeyPress = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  }, [handleSubmit]);

  const handleOpenBooking = useCallback(() => {
    window.open('https://admin.booking.com', '_blank');
  }, []);

  // Determine if submit button should be enabled
  const isSubmitDisabled = unitType === 'single' 
    ? !hasValidPropertyIds || isLoading
    : !isSinglePropertyIdValid || isLoading;

  // Get button text based on state
  const getButtonText = () => {
    if (hasValidationErrors) {
      return t(T_KEYS.VERIFY_CONNECTION_AGAIN);
    }
    return t(T_KEYS.VERIFY_CONNECTION);
  };

  // Render for 'single' unit type (multiple separated units)
  if (unitType === 'single') {
    return (
      <Container>
        <Header>
          <Title>{t(T_KEYS.ENTER_PROPERTY_IDS)}</Title>
          <Description>{t(T_KEYS.ENTER_PROPERTY_IDS_DESC)}</Description>
        </Header>

        <InputsContainer>
          {propertyIds.map((field, index) => {
            const fieldError = getFieldError(field.value) || field.error;
            const showDeleteButton = propertyIds.length > 1 || field.value.length > 0;
            
            return (
              <PropertyIdInputSection key={field.id} $hasError={!!fieldError}>
                <InputLabelRow>
                  <InputLabel>{t(T_KEYS.PROPERTY_ID_LABEL, { index: index + 1 })}</InputLabel>
                </InputLabelRow>
                <InputRow>
                  <InputWrapper $hasError={!!fieldError} $isVerified={field.verificationStatus === 'verified'}>
                    <Input
                      type="text"
                      placeholder={t(T_KEYS.PROPERTY_ID_PLACEHOLDER)}
                      value={field.value}
                      onChange={(e) => handlePropertyIdChange(field.id, e.target.value)}
                      onBlur={() => handlePropertyIdBlur(field.id)}
                      onKeyPress={handleKeyPress}
                      disabled={isLoading || field.verificationStatus === 'verifying'}
                    />
                    {field.verificationStatus === 'verifying' && (
                      <VerificationIndicator>
                        <CircularProgress size={16} sx={{ color: '#5B2FF9' }} />
                      </VerificationIndicator>
                    )}
                    {field.verificationStatus === 'verified' && (
                      <VerificationIndicator>
                        <VerifiedIcon />
                      </VerificationIndicator>
                    )}
                  </InputWrapper>
                  {showDeleteButton && (
                    <DeleteButton 
                      type="button"
                      onClick={() => handleRemovePropertyId(field.id)}
                      disabled={propertyIds.length === 1 && !field.value}
                    >
                      <TrashIcon />
                    </DeleteButton>
                  )}
                </InputRow>
                {fieldError && (
                  <ErrorRow>
                    <ErrorIcon />
                    <ErrorText>{fieldError}</ErrorText>
                  </ErrorRow>
                )}
              </PropertyIdInputSection>
            );
          })}

          <AddPropertyButton type="button" onClick={handleAddPropertyId}>
            <PlusIcon />
            <span>{t(T_KEYS.ADD_ANOTHER_PROPERTY_ID)}</span>
          </AddPropertyButton>
        </InputsContainer>

        <ButtonsSection>
          <SubmitButton 
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
            $disabled={isSubmitDisabled}
          >
            {isLoading ? (
              <CircularProgress size={20} sx={{ color: 'white' }} />
            ) : (
              getButtonText()
            )}
          </SubmitButton>

          <OpenBookingLink onClick={handleOpenBooking}>
            <ExternalLinkIcon />
            <span>{t(T_KEYS.OPEN_BOOKING_WEBSITE)}</span>
          </OpenBookingLink>
        </ButtonsSection>
      </Container>
    );
  }

  // Render for 'multi' unit type (single property with multiple units)
  return (
    <Container>
      <Header>
        <Title>{t(T_KEYS.ENTER_PROPERTY_ID)}</Title>
        <Description>{t(T_KEYS.ENTER_PROPERTY_ID_DESC)}</Description>
      </Header>

      <InputsContainer>
        <PropertyIdInputSection $hasError={!!error}>
          <InputLabelRow>
            <InputLabel>{t(T_KEYS.PROPERTY_ID_LABEL, { index: '' })}</InputLabel>
          </InputLabelRow>
          <InputWrapper $hasError={!!error}>
            <Input
              type="text"
              placeholder={t(T_KEYS.PROPERTY_ID_PLACEHOLDER)}
              value={singlePropertyId}
              onChange={handleSinglePropertyIdChange}
              onKeyPress={handleKeyPress}
              disabled={isLoading}
            />
          </InputWrapper>
          {error && (
            <ErrorRow>
              <ErrorIcon />
              <ErrorText>{error}</ErrorText>
            </ErrorRow>
          )}
        </PropertyIdInputSection>
      </InputsContainer>

      <ButtonsSection>
        <SubmitButton 
          onClick={handleSubmit}
          disabled={isSubmitDisabled}
          $disabled={isSubmitDisabled}
        >
          {isLoading ? (
            <CircularProgress size={20} sx={{ color: 'white' }} />
          ) : (
            t(T_KEYS.VERIFY_CONNECTION)
          )}
        </SubmitButton>

        <OpenBookingLink onClick={handleOpenBooking}>
          <ExternalLinkIcon />
          <span>{t(T_KEYS.OPEN_BOOKING_WEBSITE)}</span>
        </OpenBookingLink>
      </ButtonsSection>
    </Container>
  );
};

// Styled Components
const Container = styled.div`
  background: white;
  border-radius: 24px;
  padding: 40px 32px;
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 40px;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Title = styled.h2`
  font-weight: 700;
  font-size: 28px;
  line-height: 36px;
  color: #262626;
  margin: 0;
  font-family: 'IBM Plex Sans Arabic', sans-serif;
`;

const Description = styled.p`
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: #666666;
  margin: 0;
  font-family: 'IBM Plex Sans Arabic', sans-serif;
`;

const InputsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const PropertyIdInputSection = styled.div<{ $hasError?: boolean }>`
  background: #f8f8f8;
  border-radius: 20px;
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  border: 1px solid ${({ $hasError }) => $hasError ? '#d32f2f' : 'transparent'};
`;

const InputLabelRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
`;

const InputLabel = styled.label`
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: #262626;
  font-family: 'IBM Plex Sans Arabic', sans-serif;
`;

const InputRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const DeleteButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  opacity: 0.6;
  transition: opacity 0.2s ease;

  &:hover:not(:disabled) {
    opacity: 1;
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

const InputWrapper = styled.div<{ $hasError?: boolean; $isVerified?: boolean }>`
  flex: 1;
  display: flex;
  align-items: center;
  position: relative;
  background: white;
  border: 1px solid ${({ $hasError, $isVerified }) => 
    $hasError ? '#d32f2f' : $isVerified ? '#4CAF50' : 'transparent'};
  border-radius: 16px;
  padding: 12px 16px;
  height: 48px;
  
  &:focus-within {
    border-color: ${({ $hasError }) => $hasError ? '#d32f2f' : '#5b2ff9'};
  }
`;

const Input = styled.input`
  flex: 1;
  border: none;
  background: transparent;
  font-size: 16px;
  line-height: 24px;
  color: #262626;
  outline: none;
  font-family: 'IBM Plex Sans Arabic', sans-serif;
  text-align: start;

  &::placeholder {
    color: #666666;
  }

  &:disabled {
    opacity: 0.6;
  }
`;

const ErrorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  justify-content: flex-start;
`;

const ErrorText = styled.span`
  font-size: 12px;
  line-height: 16px;
  color: #d32f2f;
  font-family: 'IBM Plex Sans Arabic', sans-serif;
`;

const VerificationIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-inline-start: 8px;
`;

const AddPropertyButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  height: 44px;
  background: transparent;
  border: 2px solid #5b2ff9;
  border-radius: 14px;
  cursor: pointer;
  color: #5b2ff9;
  font-size: 15px;
  font-weight: 500;
  line-height: 44px;
  font-family: 'IBM Plex Sans Arabic', sans-serif;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(91, 47, 249, 0.05);
  }
`;

const ButtonsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
`;

const SubmitButton = styled.button<{ $disabled?: boolean }>`
  width: 100%;
  height: 56px;
  background: ${({ $disabled }) => $disabled ? '#dddddd' : '#5b2ff9'};
  color: ${({ $disabled }) => $disabled ? '#bdbdbd' : 'white'};
  border: none;
  border-radius: 16px;
  font-weight: 600;
  font-size: 18px;
  line-height: 56px;
  cursor: ${({ $disabled }) => $disabled ? 'not-allowed' : 'pointer'};
  transition: background 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'IBM Plex Sans Arabic', sans-serif;

  &:hover:not(:disabled) {
    background: ${({ $disabled }) => $disabled ? '#dddddd' : '#4a25d6'};
  }
`;

const OpenBookingLink = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: none;
  padding: 12px;
  cursor: pointer;
  color: #0052cc;
  font-size: 14px;
  line-height: 20px;
  font-family: 'IBM Plex Sans Arabic', sans-serif;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
  }

  span {
    text-decoration: none;
  }
`;

export default BookingPropertyIdStep;
